import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Volume2, VolumeX } from "lucide-react";
import { useContent } from "@/lib/ContentProvider";

/**
 * AmbientMusic — bouton flottant discret pour l'ambiance sonore.
 *
 * - Source par défaut : /media/audio/ambience.{mp3,ogg} (public).
 * - Override admin : `content.siteSounds.ambient` peut pointer vers un
 *   fichier uploadé via l'admin (résolu par `mediaUrl`).
 * - Autoplay coupé par défaut (règles navigateur). Un premier clic
 *   utilisateur lance la lecture.
 * - Persistance : localStorage (`cr-ambient-on`).
 * - Caché sur la page Entry `/` et sur l'admin `/admin`.
 */

const DEFAULT_MP3 = "/media/audio/ambience.mp3";
const DEFAULT_OGG = "/media/audio/ambience.ogg";
const TARGET_VOLUME = 0.32;
const FADE_MS = 700;
const STORAGE_KEY = "cr-ambient-on";

export default function AmbientMusic() {
  const location = useLocation();
  const audioRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const { content, mediaUrl } = useContent();
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch (_e) { return false; }
  });
  // Résout l'URL du son ambiant depuis l'admin, avec fallback statique
  const customAmbient = content?.siteSounds?.ambient;
  const ambientSrc = customAmbient ? mediaUrl(customAmbient) : DEFAULT_MP3;
  const ambientSrcOgg = customAmbient ? null : DEFAULT_OGG;

  // Ne pas afficher / jouer sur la porte d'entrée, /admin, /couchage/*, /bon-cadeau/*
  const path = location.pathname;
  const hidden = path === "/" || path.startsWith("/admin") || path.startsWith("/couchage/") || path.startsWith("/formulaire-couchage") || path.startsWith("/bon-cadeau/");

  // Fondu doux
  const fadeTo = (target) => {
    const el = audioRef.current;
    if (!el) return;
    if (fadeTimerRef.current) {
      clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    const start = el.volume;
    const startTime = performance.now();
    fadeTimerRef.current = setInterval(() => {
      const t = Math.min(1, (performance.now() - startTime) / FADE_MS);
      el.volume = start + (target - start) * t;
      if (t >= 1) {
        clearInterval(fadeTimerRef.current);
        fadeTimerRef.current = null;
        if (target === 0) {
          try { el.pause(); } catch (_e) { /* ignore */ }
        }
      }
    }, 40);
  };

  // Réagit aux changements de `on`
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (on && !hidden) {
      el.volume = 0;
      el.play().then(() => fadeTo(TARGET_VOLUME)).catch(() => {
        // Autoplay bloqué : on remet l'état à off pour cohérence UX
        setOn(false);
        try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
      });
    } else {
      fadeTo(0);
    }
  }, [on, hidden]);

  // Persistance
  useEffect(() => {
    try {
      if (on) localStorage.setItem(STORAGE_KEY, "1");
      else localStorage.removeItem(STORAGE_KEY);
    } catch (_e) { /* ignore */ }
  }, [on]);

  // Nettoyage
  useEffect(() => () => {
    if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
  }, []);

  if (hidden) {
    return (
      <audio
        ref={audioRef}
        loop
        preload="auto"
        aria-hidden="true"
      >
        <source src={ambientSrc} type="audio/mpeg" />
        {ambientSrcOgg && <source src={ambientSrcOgg} type="audio/ogg" />}
      </audio>
    );
  }

  return (
    <>
      <audio
        ref={audioRef}
        loop
        preload="auto"
        aria-hidden="true"
        data-testid="ambient-audio"
      >
        <source src={ambientSrc} type="audio/mpeg" />
        {ambientSrcOgg && <source src={ambientSrcOgg} type="audio/ogg" />}
      </audio>

      <button
        type="button"
        onClick={() => setOn((v) => !v)}
        data-testid="ambient-toggle"
        aria-label={on ? "Couper le son ambiant" : "Activer le son ambiant"}
        aria-pressed={on}
        className="fixed bottom-5 left-5 md:bottom-6 md:left-6 z-40 flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full backdrop-blur-md transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cr-ruby)]/40 hover:scale-105"
        style={{
          background: on ? "var(--cr-ruby)" : "rgba(255, 253, 249, 0.85)",
          border: `1px solid ${on ? "var(--cr-ruby)" : "rgba(139, 37, 48, 0.28)"}`,
          color: on ? "#FAF4EC" : "var(--cr-ruby)",
          boxShadow: on
            ? "0 6px 18px rgba(139, 37, 48, 0.28)"
            : "0 3px 12px rgba(46, 31, 24, 0.10)",
        }}
      >
        {on ? (
          <Volume2 size={17} strokeWidth={1.5} />
        ) : (
          <VolumeX size={17} strokeWidth={1.5} />
        )}
        {/* Halo animé quand actif */}
        {on && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              boxShadow: "0 0 0 0 rgba(139, 37, 48, 0.45)",
              animation: "cr-ambient-pulse 2.4s ease-out infinite",
            }}
          />
        )}
      </button>
    </>
  );
}
